/**
 * Reusable debounced-typeahead lookup field used by the Create Plan
 * modal for Advertiser / Brand / Contact picks. The Salesforce-mirror
 * search lives on the server; this component handles only the UX side
 * — debounce, dropdown, spinner, click-outside dismissal, keyboard nav.
 *
 * State machine the parent owns: `value` is the picked SF id (or null),
 * `displayName` is the matching name to render in the read-only chip
 * when collapsed. Picking calls `onChange(id, name, extra?)` where
 * `extra` is any extra payload the search returned for that item
 * (Contact uses it to ferry the Account info up to the parent so the
 * derived Agency line can render).
 *
 * Why parent owns both `value` and `displayName`: the search endpoint
 * returns items as {id, name}, so when the parent is hydrated from
 * elsewhere (roll-forward pre-pop, edit-existing) it already knows
 * both. Avoids a "resolve id → name" round-trip just to render.
 */

import React, { useEffect, useRef, useState } from 'react';

/**
 * Shape every search result must satisfy. The actual searchFn return
 * type can extend this with extra fields (e.g. Contact carries
 * accountId/accountName).
 */
export interface LookupSearchItem {
  id: string;
  name: string;
}

interface LookupFieldProps<T extends LookupSearchItem> {
  /** The picked SF id, or null if nothing picked. */
  value: string | null;
  /** Display name for the picked item; rendered when `value` is set. */
  displayName: string | null;
  /**
   * Debounced search function. Receives the current input text after
   * the debounce window settles. Should return a small list (~20
   * items) sorted in the order they should appear.
   */
  searchFn: (q: string) => Promise<T[]>;
  /**
   * Fires when the user picks an item from the dropdown. `extra` is
   * the full search item (so Contact can hand accountId/accountName
   * to the parent in one shot).
   */
  onChange: (id: string, name: string, extra: T) => void;
  /** Clear-the-pick callback (×). Parent decides if clearing is allowed. */
  onClear?: () => void;
  /** Field label shown above the input. */
  label: string;
  /** Star next to the label + red border when empty. */
  required?: boolean;
  /** Placeholder for the input when empty. */
  placeholder?: string;
  /** Disabled state — used for Brand when no Advertiser picked yet. */
  disabled?: boolean;
  /** Tooltip shown when disabled (e.g. "Pick an Advertiser first"). */
  disabledTitle?: string;
  /**
   * Optional override for the empty-results dropdown row. Receives the
   * current input text (already trimmed) and returns text to render in
   * the dropdown when the search returned zero rows. `null` falls back
   * to the default ("No matches" / "Type to search"). Set `error: true`
   * to render in red — used by the Brand field to flag "this advertiser
   * has no brands matching your query" as a dead-end the user should
   * resolve (pick a different advertiser or add the brand in SF first),
   * distinct from a benign "keep typing" empty state.
   */
  emptyResultsMessage?: (query: string) => { text: string; error?: boolean } | null;
  /**
   * Optional per-row render override. Receives the search item and
   * returns the ReactNode to render inside the row. Used by the
   * combined Agency+Buyer picker to render `<b>Starcom</b> — Lori
   * Robinson-Hogan` per row while keeping the item-name flat display
   * elsewhere. Defaults to `item.name` when omitted.
   */
  renderItem?: (item: T) => React.ReactNode;
  /**
   * Optional display-value override for the collapsed input when a
   * value is picked. Receives the `displayName` prop verbatim; use
   * this when the parent wants a composed display (e.g. "Starcom —
   * Lori Robinson-Hogan") without changing the search-item shape.
   * Defaults to `displayName` when omitted.
   */
  formatSelectedValue?: (displayName: string) => string;
  /**
   * When true, pins a "TBD" placeholder row at the TOP of the
   * dropdown, always visible regardless of what the user typed. Used
   * on the Advertiser / Brand / Agency-Buyer pickers to let users
   * mark a plan as still-in-negotiation without linking to a real SF
   * entity. TBD-picked plans are blocked from Push-to-SF downstream.
   */
  showTbdOption?: boolean;
  /**
   * Parent tells us "the current pick IS the TBD placeholder" — used
   * to collapse the input to "TBD" and surface the × clear even
   * though `value` is null (TBD has no SF id). Without this, a
   * TBD-picked field would look empty.
   */
  isTbdPicked?: boolean;
  /**
   * Fires when the user picks the pinned TBD row. Parent should set
   * its display name to "TBD" and its SF id to null.
   */
  onSelectTbd?: () => void;
}

// Style constants kept local — match the existing modal recipes.
const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px',
  border: '1px solid #dde1e9', borderRadius: 6,
  fontSize: 13,
};
const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
  background: '#fff', border: '1px solid #dde1e9', borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  zIndex: 20,
  maxHeight: 200, overflowY: 'auto',
};

export function LookupField<T extends LookupSearchItem>({
  value, displayName, searchFn, onChange, onClear,
  label, required, placeholder, disabled, disabledTitle,
  emptyResultsMessage, renderItem, formatSelectedValue,
  showTbdOption, isTbdPicked, onSelectTbd,
}: LookupFieldProps<T>): React.ReactElement {
  // `query` is the current input text. When `value` is set, the input
  // displays `displayName` and the dropdown is closed — typing into it
  // implicitly clears the pick and re-opens the dropdown.
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Ref to the currently-highlighted dropdown row so we can scroll it
  // into view when the dropdown opens on an already-picked value.
  // Matches the native OS behavior where opening a filled select
  // reveals the current pick centered in the list.
  const currentRowRef = useRef<HTMLDivElement | null>(null);

  // Show the picked name in the input when a value is set + the
  // dropdown isn't actively being typed into. When the user types, we
  // route to `query` instead so backspace clears it cleanly.
  // `formatSelectedValue` lets the parent compose a display string
  // (e.g. "Starcom — Lori Robinson-Hogan") without changing the
  // search-item shape.
  //
  // TBD is a "picked" state without an SF id — the parent flags it via
  // `isTbdPicked` and we treat it the same as a value-set state for
  // display purposes (collapsed input shows "TBD", × clear surfaces).
  const isCollapsedPick = !!value || !!isTbdPicked;
  const displayForCollapsed = isCollapsedPick
    ? (formatSelectedValue ? formatSelectedValue(displayName ?? '') : (displayName ?? ''))
    : '';
  const inputValue = isCollapsedPick && !open ? displayForCollapsed : query;

  // Debounced search — 250ms window keeps Snowflake load reasonable
  // while still feeling responsive. Triggers on every `query` change
  // while the dropdown is open.
  useEffect(() => {
    if (!open) return;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const items = await searchFn(query);
        setResults(items);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query, open, searchFn]);

  // Click-outside dismissal — only registered while the dropdown is
  // open to keep the global click listener footprint tiny.
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // When the dropdown opens on an already-picked value, scroll that
  // row into view + center it in the scrollable dropdown. Waits for
  // results to actually populate (the search fires on open with the
  // empty query and comes back a tick later). Effect re-runs on
  // results changes so the scroll happens the first frame the row
  // exists in the DOM.
  useEffect(() => {
    if (!open || !value || results.length === 0) return;
    // rAF so the ref has actually been attached in this render pass.
    const raf = requestAnimationFrame(() => {
      const row = currentRowRef.current;
      if (row) row.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [open, value, results]);

  function commitPick(item: T) {
    onChange(item.id, item.name, item);
    setOpen(false);
    setQuery('');
    // Blur the input so focus doesn't visually linger on the field
    // after a pick — user shouldn't have to click elsewhere to
    // dismiss the "active" outline.
    inputRef.current?.blur();
  }

  function clear() {
    if (onClear) onClear();
    setQuery('');
    setOpen(true);
    // Keep focus on the input after clear so the user can immediately
    // type a new search — don't blur here.
    inputRef.current?.focus();
  }

  const isEmpty = !value;
  const errored = required && isEmpty;

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <label style={labelStyle}>
        {label}{required ? ' *' : ''}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          value={inputValue}
          placeholder={placeholder ?? 'Type to search…'}
          disabled={disabled}
          title={disabled ? disabledTitle : undefined}
          onFocus={e => {
            if (disabled) return;
            setOpen(true);
            // Select-all on focus so typing immediately replaces the
            // picked value — matches browser URL-bar behavior. Users
            // clicking into a filled field to change their mind don't
            // have to backspace or triple-click first. requestAnimationFrame
            // avoids the browser's default cursor-placement racing our select.
            const el = e.currentTarget;
            requestAnimationFrame(() => { try { el.select(); } catch { /* ignore */ } });
          }}
          onChange={e => {
            const v = e.target.value;
            setQuery(v);
            setOpen(true);
            // First keystroke after a pick implicitly clears the value
            // so the input becomes a fresh search rather than a stale
            // display. Parent's onClear (if provided) wipes the id
            // (and also the TBD state when we're in one).
            if ((value || isTbdPicked) && onClear) onClear();
          }}
          style={{
            ...inputStyle,
            border: errored ? '1.5px solid #fca5a5' : inputStyle.border,
            background: disabled ? '#f3f4f6' : '#fff',
            color: disabled ? '#9ca3af' : '#111827',
            cursor: disabled ? 'not-allowed' : 'text',
            paddingRight: isCollapsedPick ? 28 : inputStyle.padding,
          }}
        />
        {isCollapsedPick && !disabled && (
          // Clear button — visible for both a real SF pick AND a TBD
          // placeholder pick so users can back out of either.
          <button
            type="button"
            onClick={clear}
            aria-label="Clear selection"
            style={{
              position: 'absolute', right: 6, top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent', border: 'none',
              color: '#6b7280', cursor: 'pointer',
              fontSize: 14, padding: '2px 6px',
            }}
          >×</button>
        )}
      </div>

      {open && !disabled && (
        <div style={dropdownStyle}>
          {/* Pinned TBD placeholder — always at the top when enabled,
              regardless of what the user typed. Distinct styling
              (gray band + PLACEHOLDER badge) signals "this is not a
              real SF entity"; downstream code blocks push-to-SF when
              a plan's fields resolve to TBD. */}
          {showTbdOption && (
            <div
              onMouseDown={e => {
                e.preventDefault();
                if (onSelectTbd) onSelectTbd();
                setOpen(false);
                setQuery('');
                inputRef.current?.blur();
              }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600,
                background: isTbdPicked ? '#e5e7eb' : '#f3f4f6',
                borderBottom: '1px solid #d1d5db',
                color: '#374151',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#e5e7eb')}
              onMouseLeave={e => (e.currentTarget.style.background = isTbdPicked ? '#e5e7eb' : '#f3f4f6')}
            >
              <span>TBD</span>
              <span style={{
                fontSize: 9.5, fontWeight: 600, letterSpacing: '0.05em',
                color: '#6b7280', textTransform: 'uppercase',
                background: '#fff', border: '1px solid #d1d5db',
                borderRadius: 3, padding: '2px 6px',
              }}>Placeholder</span>
            </div>
          )}
          {loading && (
            <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{
                width: 12, height: 12,
                border: '2px solid #dbeafe',
                borderTopColor: '#2563eb',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
                display: 'inline-block',
              }} />
              Searching…
            </div>
          )}
          {!loading && results.length === 0 && !showTbdOption && (() => {
            // Custom empty-results renderer takes precedence — used by
            // Brand to flag "advertiser has no matching brands" in red.
            // Falls back to the generic "No matches" / "Type to search"
            // hint when the parent doesn't provide one or explicitly
            // returns null.
            const custom = emptyResultsMessage?.(query.trim());
            if (custom && custom.text) {
              return (
                <div style={{
                  padding: '10px 12px', fontSize: 12,
                  color: custom.error ? '#dc2626' : '#6b7280',
                  fontStyle: custom.error ? 'normal' : 'italic',
                  fontWeight: custom.error ? 500 : 400,
                }}>
                  {custom.text}
                </div>
              );
            }
            return (
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                {query.trim() ? 'No matches' : 'Type to search'}
              </div>
            );
          })()}
          {!loading && results
            // When the TBD placeholder row is enabled, suppress any REAL
            // SF record whose name is literally "TBD". Salesforce has
            // several ("TBD" advertiser, "TBD — TBD" contact, etc.) and
            // showing them next to the pinned placeholder made the list
            // read as two identical "TBD" rows — one that correctly
            // blocks push-to-SF, one that silently doesn't. Picking the
            // wrong one produced a plan that LOOKED like a TBD draft but
            // sailed past every TBD gate.
            //
            // Exact match only (trimmed, case-insensitive) so genuine
            // records that merely CONTAIN "TBD" — "TBD Mortgage Company",
            // "Doctor Butlers TBD" — stay selectable.
            .filter(item => !(showTbdOption
              && (item.name ?? '').trim().toUpperCase() === 'TBD'))
            .map(item => {
            const isCurrent = value != null && item.id === value;
            return (
              <div
                key={item.id}
                // Ref only the current-pick row so we can scroll it
                // into view on open (see effect below).
                ref={el => { if (isCurrent) currentRowRef.current = el; }}
                onMouseDown={(e) => {
                  // mousedown (not click) — fires before the input loses
                  // focus, otherwise the click-outside handler would
                  // close the dropdown before the pick registers.
                  e.preventDefault();
                  commitPick(item);
                }}
                style={{
                  padding: '8px 12px', cursor: 'pointer', fontSize: 13,
                  borderTop: '1px solid #f1f5f9',
                  // Subtle blue tint on the current pick so the user
                  // can spot "you already have this one" and arrow to
                  // a neighbor without hunting.
                  background: isCurrent ? '#eff4ff' : undefined,
                  fontWeight: isCurrent ? 600 : 400,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#eff4ff')}
                onMouseLeave={e => (e.currentTarget.style.background = isCurrent ? '#eff4ff' : '')}
              >
                {renderItem ? renderItem(item) : item.name}
              </div>
            );
          })}
        </div>
      )}

      {/* Spinner keyframes — declared inline so the component is self-contained. */}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
